// ** Angular Core & Common Imports
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  computed,
  inject,
  ElementRef,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

// ** Types Import
import { ButtonConfig, ButtonRoute } from './button.types';

// ** Service Import
import { WebsiteService } from '@shared/services/website.service';

// ** Third-Party Imports
import { TranslocoPipe } from '@jsverse/transloco';

// ** Pipe Imports
import { CdnUrlPipe } from '@shared/pipes/cdn-url.pipe';


@Component({
  selector: 'app-button',
  standalone: true,
  imports: [
    CommonModule, 
    RouterModule, 
    TranslocoPipe,
    CdnUrlPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
})

export class ButtonComponent {
  websiteService = inject(WebsiteService);

  @Input({ required: true }) buttonConfig!: ButtonConfig;
  @Input() route?: ButtonRoute;
  @Input() customTemplate?: TemplateRef<any>;

  @Output() click = new EventEmitter<Event>();
  
  constructor(private host: ElementRef) {}
  hasProjectedContent = false;

  ngAfterContentInit(): void {
    const content = this.host.nativeElement.querySelector('ng-content');
    this.hasProjectedContent = !!content?.textContent?.trim();
  }

  handleClick(event: Event) {
    if (this.buttonConfig.disabled || !this.route?.link) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    this.click.emit(event);
  }

  // ** Computed Properties
  get buttonTag(): 'button' | 'routerLink' | 'externalLink' {
    if (!this.route?.link) return 'button';
    return this.isExternalUrl(this.route?.link) ? 'externalLink' : 'routerLink';
  }

  isExternalUrl(url: string | any[]): boolean {
    return typeof url === 'string' && /^(http|https):\/\//.test(url);
  }

  get arrowIconUrl(): string {
    switch (this.buttonConfig.arrowStyle) {
      case 'white':
        return 'arrow-right-white.svg';
      case 'green':
        return 'arrow-right-accent.svg';
      default:
        return 'arrow-right.svg';
    }
  }

  getButtonClasses = computed(() => {
    let classes = this.getBaseClassesForButton();

    if (this.buttonConfig.disabled) {
      classes += ` ${this.getDisabledButtonClasses()}`;
    } else {
      switch (this.buttonConfig.type) {
        case 'primary':
          classes += ' bg-primary-500 text-base-white hover:bg-primary-600';
          break;
        case 'primaryOutline':
          classes +=
            ' border border-primary-500 hover:bg-body-light hover:border-primary-600 hover:text-primary-600';
          break;
        case 'secondary':
          classes += ' bg-gray-900 text-base-white hover:bg-primary-500';
          break;
        case 'ghost':
          classes +=
            ' bg-white text-base-main border border-gray-400 hover:border-gray-900';
          break;
      }

      if (this.buttonConfig.customClasses) {
        classes += ` ${this.buttonConfig.customClasses}`;
      }
    }

    return classes;
  });

  getBaseClassesForButton(): string {
    return `
      flex px-8 items-center justify-center gap-3 rounded-full 
      font-arial-mt-bold leading-[120%]
      h-[46px] lg:h-14 text-xl lg:text-2xl
      py-3 lg:py-4
    `;
  }

  getDisabledButtonClasses(): string {
    return this.buttonConfig.type === 'ghost'
      ? 'border border-gray-300 text-gray-300 cursor-not-allowed'
      : 'bg-gray-300 text-base-white cursor-not-allowed';
  }
}